import { PaymentTotal } from '../interfaces/payment-total.interface';
import { ExtendedFlightBasket } from '../interfaces/upgrades.interface';
import { PaymentType } from '../interfaces/payment-type.interface';
import { PaymentOptionV2 } from '../interfaces/payment-option.interface';
import { VoucherType, Buyer, SupportedUpgrades, PaymentOptionsV2, PaymentV2, UpgradeProduct } from '../api/models';
import { getPaymentFromFlight, getUpgradeFromFlight } from './data.utils';

const EMPTY_PAYMENT_TOTAL: PaymentTotal = {
  [PaymentType.points]: 0,
  [PaymentType.money]: 0,
  [PaymentType.LONG_HAUL_UPGRADE]: 0,
  [PaymentType.EUROPE_UPGRADE]: 0,
  [PaymentType.UPGRADE_BENEFIT]: 0,
  currency: '',
};

/**
 * Calculate the total payment of the selected Flight basket
 * @param selectedFlights - flightBasket array
 * @param passengerCount - number of passengers selected
 */
export const calculatePaymentTotal = (
  selectedFlights: ExtendedFlightBasket[],
  passengerCount: number,
  buyer: Buyer
): PaymentTotal => {
  return selectedFlights.reduce(
    (acc: PaymentTotal, extendedFlight: ExtendedFlightBasket) => {
      const payment = getPaymentFromFlight(
        extendedFlight.flight,
        extendedFlight.productName,
        extendedFlight.paymentType
      );

      if (extendedFlight.paymentType === PaymentType.money) {
        const paymentAsMoney = payment as PaymentOptionsV2['money'];

        acc.money += parseFloat(paymentAsMoney.price) * passengerCount;
        acc.currency = paymentAsMoney.currency;
      } else if (extendedFlight.paymentType === PaymentType.points) {
        const paymentAsPoints = payment as PaymentOptionsV2['points'];
        if (
          isLongHaulUpgraded(selectedFlights, extendedFlight.flight.segmentInfo.group) &&
          !extendedFlight.flight.segmentInfo.longHaul &&
          paymentAsPoints.priceWithLongHaul !== undefined
        ) {
          acc.points += paymentAsPoints.priceWithLongHaul * passengerCount;
        } else {
          acc.points += paymentAsPoints.price * passengerCount;
        }
      } else if (extendedFlight.paymentType === PaymentType.EUROPE_UPGRADE) {
        const paymentAsVoucher = payment as PaymentOptionsV2[PaymentType.EUROPE_UPGRADE];

        acc[PaymentType.EUROPE_UPGRADE] += paymentAsVoucher.price * passengerCount;
      } else if (extendedFlight.paymentType === PaymentType.UPGRADE_BENEFIT) {
        const paymentAsVoucher = payment as PaymentOptionsV2[PaymentType.UPGRADE_BENEFIT];
        const originalEuropeVouchers =
          buyer.vouchers.find((voucher) => voucher.type === VoucherType.EuropeUpgrade)?.count || 0;

        acc[PaymentType.EUROPE_UPGRADE] += paymentAsVoucher.price * passengerCount;

        if (acc[PaymentType.EUROPE_UPGRADE] > originalEuropeVouchers) {
          acc[PaymentType.UPGRADE_BENEFIT] += acc[PaymentType.EUROPE_UPGRADE] - originalEuropeVouchers;
          acc[PaymentType.EUROPE_UPGRADE] = originalEuropeVouchers;
        }
      } else if (extendedFlight.paymentType === PaymentType.LONG_HAUL_UPGRADE) {
        const paymentAsVoucher = payment as PaymentOptionsV2[PaymentType.LONG_HAUL_UPGRADE];

        acc[PaymentType.LONG_HAUL_UPGRADE] += paymentAsVoucher.price * passengerCount;
      }

      return acc;
    },
    { ...EMPTY_PAYMENT_TOTAL }
  );
};

/**
 * calculate the minimum payment amount for 1 flight
 * @param paymentOptions - array of paymentOptions to look through
 */
export const calculateMinimumPayment = (paymentOptions: PaymentOptionsV2[]): PaymentTotal => {
  return paymentOptions.reduce(
    (acc, cur) => {
      let money = acc.money;
      let currency = acc.currency;
      let points = acc.points;

      if (cur.money?.price) {
        money = money ? Math.min(+cur.money.price, money) : +cur.money.price;
        currency = cur.money.currency;
      }
      if (cur.points?.price) {
        points = points ? Math.min(+cur.points.price, points) : +cur.points.price;
      }

      return {
        money,
        currency,
        points,
        [PaymentType.LONG_HAUL_UPGRADE]: acc[PaymentType.LONG_HAUL_UPGRADE] || cur.LONG_HAUL_UPGRADE?.price,
        [PaymentType.EUROPE_UPGRADE]: acc[PaymentType.EUROPE_UPGRADE] || cur.EUROPE_UPGRADE?.price,
        [PaymentType.UPGRADE_BENEFIT]: acc[PaymentType.UPGRADE_BENEFIT] || cur.UPGRADE_BENEFIT?.price,
      };
    },
    { ...EMPTY_PAYMENT_TOTAL }
  );
};

/**
 * Check if a longhaul flight in the group is already selected for upgrade with points
 * @param selectedFlights array of flights in the basket
 * @param group group number of current flight
 */
export const isLongHaulUpgraded = (flightBasket: ExtendedFlightBasket[], group: number): boolean => {
  return Boolean(
    flightBasket.find(({ flight, paymentType, productName }) => {
      return (
        flight.segmentInfo.group === group &&
        paymentType === PaymentType.points &&
        productName === UpgradeProduct.EconomyToBusiness &&
        flight.segmentInfo.longHaul
      );
    })
  );
};

/**
 * Determine paymentType from payment object
 * @param payment object to check
 */
export const getPaymentType = (payment: PaymentV2): PaymentType => (Object.keys(payment)[0] as PaymentType) || null;

/**
 * Get count of available vouchers of specified type
 * @param voucherType
 * @param user
 */
export const getVoucherAmount = (voucherType: VoucherType, user: Buyer = {}): number => {
  return user?.vouchers?.find((v) => v.type === voucherType)?.count ?? 0;
};

/**
 * get count of number of vouchers of given type buyer has
 *
 * @param buyer - users with vouchers to check
 * @param type - type of voucher to check for
 */
export function getBuyerVouchersCount(buyer: Buyer, type: VoucherType): number {
  if (!buyer || !buyer.vouchers) {
    return 0;
  }
  const vouchers = buyer.vouchers.find((x) => x.type === type);
  return vouchers ? vouchers.count : 0;
}

/**
 * Create a PaymentExtended object from a PaymentOption
 * @param paymentOption
 */
export function createPaymentObject(
  flightsBasket: ExtendedFlightBasket[],
  flightBasket: ExtendedFlightBasket
): PaymentV2 {
  const payments: PaymentV2 = {};
  const upgrade = getUpgradeFromFlight(flightBasket.flight, flightBasket.productName);
  const paymentType = flightBasket.paymentType;

  if (paymentType === PaymentType.money) {
    payments.money = {
      currency: upgrade.paymentOptions[paymentType].currency,
      price: upgrade.paymentOptions[paymentType].price,
    };
  } else if (paymentType === PaymentType.points) {
    const longHaulDiscount = !flightBasket.flight.segmentInfo.longHaul
      ? isLongHaulUpgraded(flightsBasket, flightBasket.flight.segmentInfo.group)
      : false;
    const regularPrice = upgrade.paymentOptions[paymentType].price;
    const priceWithLongHaul = upgrade.paymentOptions[paymentType].priceWithLongHaul;

    payments.points = {
      price: longHaulDiscount && typeof priceWithLongHaul === 'number' ? priceWithLongHaul : regularPrice,
    };
  } else {
    payments[paymentType] = {
      price: upgrade.paymentOptions[paymentType].price,
    };
  }

  return payments;
}

export const isWaitlistedPaymentOption = (paymentOption: PaymentOptionV2): boolean => {
  // @todo array 0 because ECONOMY_TO_BUSINESS does not have multiple voucher options.
  const upgrade = Array.isArray(paymentOption) ? paymentOption[0] : paymentOption;

  return upgrade.supportedUpgrades === SupportedUpgrades.EconomyToBusinessWaitlist;
};
